import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ShieldCheck, RefreshCw, AlertCircle, AlertTriangle,
  Info, ChevronDown, ChevronUp, Filter, FileSearch,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLE = {
  error:   { color: "text-red-400",    icon: AlertCircle },
  warning: { color: "text-amber-400",  icon: AlertTriangle },
  info:    { color: "text-sky-400",    icon: Info },
};

const AdminOrderAuditPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
      return;
    }
    runAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAudit = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/orders/audit/all?limit=1000`);
      setReport(res.data);
    } catch (e) {
      toast.error("Failed to run audit");
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (!report?.orders) return [];
    let list = report.orders;
    if (severityFilter === "errors") {
      list = list.filter((o) => o.stats.errors > 0);
    } else if (severityFilter === "warnings") {
      list = list.filter((o) => o.stats.warnings > 0);
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (o) =>
          (o.order_number || "").toLowerCase().includes(s) ||
          (o.client_name || "").toLowerCase().includes(s) ||
          (o.garment_type || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [report, search, severityFilter]);

  return (
    <div className="min-h-screen pt-20">
      <section className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="audit-back-btn">
            <ArrowLeft size={14} className="mr-1" /> Admin
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
            <div>
              <h1 className="text-2xl font-light flex items-center gap-2">
                <ShieldCheck size={22} className="text-emerald-400" /> Order QA / Audit
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Catches typos and missing fields across all saved MTM orders before they reach the factory.
              </p>
            </div>
            <Button onClick={runAudit} disabled={loading} data-testid="audit-rerun-btn">
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Auditing…" : "Re-run audit"}
            </Button>
          </div>
        </div>
      </section>

      <section className="py-6">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Stats */}
          {report && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-card border border-white/10 rounded-lg p-4">
                <p className="text-xs text-muted-foreground">Orders checked</p>
                <p className="text-2xl font-light mt-1" data-testid="audit-stat-checked">{report.total_checked}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-lg p-4">
                <p className="text-xs text-muted-foreground">With issues</p>
                <p className="text-2xl font-light mt-1 text-amber-400" data-testid="audit-stat-with-issues">{report.orders_with_issues}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-lg p-4">
                <p className="text-xs text-muted-foreground">Total errors</p>
                <p className="text-2xl font-light mt-1 text-red-400" data-testid="audit-stat-errors">{report.total_errors}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-lg p-4">
                <p className="text-xs text-muted-foreground">Total warnings</p>
                <p className="text-2xl font-light mt-1 text-amber-400" data-testid="audit-stat-warnings">{report.total_warnings}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <FileSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search order #, client, garment type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="audit-search-input"
              />
            </div>
            <div className="flex items-center gap-1 text-xs">
              <Filter size={12} className="text-muted-foreground mr-1" />
              {["all", "errors", "warnings"].map((sev) => (
                <Button
                  key={sev}
                  variant={severityFilter === sev ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setSeverityFilter(sev)}
                  className="h-7 capitalize text-xs"
                  data-testid={`audit-filter-${sev}`}
                >
                  {sev}
                </Button>
              ))}
            </div>
          </div>

          {/* Orders list */}
          {loading && !report && (
            <div className="text-center py-12 text-muted-foreground text-sm">Running audit…</div>
          )}

          {report && filteredOrders.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm" data-testid="audit-empty-result">
              {report.orders_with_issues === 0
                ? "All orders are clean. 🎉"
                : "No orders match the current filter."}
            </div>
          )}

          <div className="space-y-2">
            {filteredOrders.map((o) => {
              const expanded = expandedId === o.id;
              return (
                <motion.div
                  key={o.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-card border border-white/10 rounded-lg overflow-hidden"
                  data-testid={`audit-order-${o.order_number}`}
                >
                  <button
                    onClick={() => setExpandedId(expanded ? null : o.id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/20"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="text-left min-w-0">
                        <p className="font-mono text-sm">{o.order_number || "—"}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {o.client_name} · {o.garment_type} · {o.order_date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {o.stats.errors > 0 && (
                        <span className="text-xs text-red-400 flex items-center gap-1">
                          <AlertCircle size={12} /> {o.stats.errors}
                        </span>
                      )}
                      {o.stats.warnings > 0 && (
                        <span className="text-xs text-amber-400 flex items-center gap-1">
                          <AlertTriangle size={12} /> {o.stats.warnings}
                        </span>
                      )}
                      {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-3 space-y-1.5 border-t border-white/5">
                      {o.issues.map((issue, idx) => {
                        const s = SEVERITY_STYLE[issue.severity] || SEVERITY_STYLE.info;
                        const Icon = s.icon;
                        return (
                          <div key={`${issue.code}-${idx}`} className="flex items-start gap-2 pt-2 text-xs">
                            <Icon size={12} className={`${s.color} shrink-0 mt-0.5`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <code className={`text-[10px] font-mono ${s.color}`}>{issue.code}</code>
                                <span className="text-[10px] text-muted-foreground">{issue.field}</span>
                                {issue.auto_fix && (
                                  <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                                    → suggested:
                                    <code className="text-amber-400/80 font-mono">{String(issue.auto_fix)}</code>
                                  </span>
                                )}
                              </div>
                              <p className="mt-0.5">{issue.message}</p>
                              {issue.suggestion && (
                                <p className="text-[11px] text-muted-foreground mt-0.5">{issue.suggestion}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminOrderAuditPage;
